import { makeAutoObservable, runInAction } from 'mobx';

import { get, patch, post, remove } from '@partsbadger/utils';

import FileDownload from 'js-file-download';

type SignedUrlResponse = { url: string };
const hasSignedUrl = (value: unknown): value is SignedUrlResponse =>
    typeof value === 'object' && value !== null && 'url' in value && typeof value.url === 'string';

class FileStore {
    fileUploaded: File | null = null;
    fileType = '';
    fileForDownload: Blob | null = null;
    threeDFileUrl = '';
    twoDFileUrl = '';
    isConverionToolTourClosed = false;
    isFileCreationTourClosed = false;
    isPartsLibraryClosed = false;

    constructor() {
        makeAutoObservable(this);
        const getConverionToolTourStatus = localStorage.getItem('tour_converionTool_closed');
        const getFileCreationTourStatus = localStorage.getItem('tour_fileCreation_closed');
        const getPartsLibraryStatus = localStorage.getItem('tour_partsLibrary_closed');

        if (getPartsLibraryStatus === 'true') {
            runInAction(() => {
                this.isPartsLibraryClosed = true;
            });
        }

        if (getConverionToolTourStatus === 'true') {
            runInAction(() => {
                this.isConverionToolTourClosed = true;
            });
        }

        if (getFileCreationTourStatus === 'true') {
            runInAction(() => {
                this.isFileCreationTourClosed = true;
            });
        }
    }

    setOpenThePartsLibraryTour() {
        this.isPartsLibraryClosed = false;
        localStorage.setItem('tour_partsLibrary_closed', 'false');
    }

    setCloseThePartsLibraryTour() {
        this.isPartsLibraryClosed = true;
        localStorage.setItem('tour_partsLibrary_closed', 'true');
    }

    setOpenTheFileCreationTour() {
        this.isFileCreationTourClosed = false;
        localStorage.setItem('tour_fileCreation_closed', 'false');
    }

    setCloseTheFileCreationTour() {
        this.isFileCreationTourClosed = true;
        localStorage.setItem('tour_fileCreation_closed', 'true');
    }

    setCloseTheConverionToolTour() {
        this.isConverionToolTourClosed = true;
        localStorage.setItem('tour_converionTool_closed', 'true');
    }

    async upload2DAnd3DFile(file: File, type: '2D' | '3D') {
        const formData = new FormData();

        formData.append('file', file);

        this.fileUploaded = file;

        this.fileType = type;

        return file;
    }

    async convert3DFile() {
        const url = '/customer/step-files/convert-new-file/';

        const formData = new FormData();
        if (!this.fileUploaded) return null;

        // const file = this.stepFile ? this.stepFile : this.drawingFile;
        const fileName = this.fileUploaded?.name?.split('.').shift();

        // console.log('fileId', file);

        formData.append('file', this.fileUploaded);

        const response = await post(`${url}`, formData, {
            responseType: 'blob',
        });

        await FileDownload(response, `${fileName}.step`);

        this.fileForDownload = response;

        return response;
    }

    async convert2DFile() {
        const url = '/customer/drawing-files/convert-new-file/';

        const formData = new FormData();
        if (!this.fileUploaded) return null;

        // const file = this.stepFile ? this.stepFile : this.drawingFile;
        const fileName = this.fileUploaded?.name.split('.').shift();

        // console.log('fileId', file);

        formData.append('file', this.fileUploaded);

        const response = await post(`${url}`, formData, {
            responseType: 'blob',
        });

        await FileDownload(response, `${fileName}.pdf`);

        this.fileForDownload = response;

        return response;
    }

    async sendFileCreation() {
        const url = '/customer/drawing-files/file-creation-request/';

        const formData = new FormData();
        if (!this.fileUploaded) return null;

        formData.append('file', this.fileUploaded);

        const response = await post(`${url}`, formData, {
            responseType: 'blob',
        });

        return response;
    }

    async cleanStore() {
        this.fileUploaded = null;
        this.fileForDownload = null;
        this.fileType = '';
    }

    async get3DSignedUrl(id: number): Promise<SignedUrlResponse> {
        const res = await get(`/customer/step-files/${id}/signed-url/`);
        if (typeof res === 'string') {
            return { url: res };
        }

        if (hasSignedUrl(res)) {
            return { url: res.url };
        }

        throw new Error('Invalid signed URL response');
    }

    // FileStore.ts
    async open3DFile(id: number, filename?: string) {
        const url = await this.get3DSignedUrl(id);
        // url could be string or {url: string} depending on backend
        const href = typeof url === 'string' ? url : url?.url;
        if (!href) return;

        window.open(href, '_blank', 'noreferrer');
    }

    async download3DFile(id: number, filename: string) {
        const hrefRaw = await this.get3DSignedUrl(id);
        const href = typeof hrefRaw === 'string' ? hrefRaw : hrefRaw?.url;
        if (!href) return;

        // download via blob so it forces download even if browser wants to preview
        const blob = await get(href, { responseType: 'blob' });
        FileDownload(blob, filename);
    }

    async get2DSignedUrl(id: number): Promise<string> {
        const response = await get(`/staff/drawing-files/${id}/signed-url/`);
        const url = typeof response === 'string' ? response : response?.url ?? '';
        this.twoDFileUrl = url;
        return url;
    }

    async open2DFile(id: number) {
        const url = await this.get2DSignedUrl(id);
        if (!url) return;
        window.open(url, '_blank', 'noreferrer');
    }

    async download2DFile(id: number, filename: string) {
        const url = await this.get2DSignedUrl(id);
        if (!url) return;

        const blob = await get(url, { responseType: 'blob' });
        FileDownload(blob, filename);
    }
}

export default new FileStore();
